// BUILD-042 (Oct 24 combined permission repairs) - shared load-state model for the capability-gated HR administration lists and actions.
//
// Why: a capability-gated list the server refuses (it answers 404 to a caller who lacks the capability, so the list's existence is not disclosed) used to be
// turned into an EMPTY list ("No templates yet", "No forms yet", ...), and New/Add/Assign stayed enabled. Loading, a successful (authorized) empty list,
// a missing capability and a real service failure are four different facts; these helpers keep them apart.
//
// Scope discipline: a 404 is NEVER remapped globally. `classifyLoadError` is called only at the specific call sites that list capability-gated data (or an
// employee-targeted record), and says which kind of call it is. UX only: the server re-authorizes every request; nothing here is an authorization decision.
import { useHrAccess } from '../HrAccessContext';

/** The exact permission labels shown to people (same wording as the Access editor). */
export const PERMISSION_LABEL = {
  checklistTemplates: 'Manage checklist templates',
  employeeChecklists: 'Manage employee checklists',
  documents: 'Manage forms, documents & equipment',
} as const;
export type PermissionLabel = typeof PERMISSION_LABEL[keyof typeof PERMISSION_LABEL];

/** The three optional `surfaces` flags GET /hr/me reports (company-scope capability held). */
export type CapabilitySurface = 'mayManageChecklistTemplates' | 'mayManageEmployeeChecklists' | 'mayManageDocuments';

export const SURFACE_LABEL: Record<CapabilitySurface, PermissionLabel> = {
  mayManageChecklistTemplates: PERMISSION_LABEL.checklistTemplates,
  mayManageEmployeeChecklists: PERMISSION_LABEL.employeeChecklists,
  mayManageDocuments: PERMISSION_LABEL.documents,
};

/**
 * One list's state. `denied` = the server refused a capability-gated list (403/404). `targetUnavailable` = an employee-targeted read answered 404, which is
 * intentionally non-enumerating (it covers "not allowed" and "no such employee" alike), so it never claims the person lacks anything and is never an empty state.
 * `unavailable` = the request itself failed (network, 5xx, anything else); it does not mean there is nothing.
 */
export type LoadView =
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'denied' }
  | { kind: 'targetUnavailable' }
  | { kind: 'unavailable'; message: string };

export const LOADING: LoadView = { kind: 'loading' };
export const READY: LoadView = { kind: 'ready' };

export function classifyLoadError(e: unknown, opts: { gatedList?: boolean; targeted?: boolean } = {}): LoadView {
  const err = e as { status?: number; message?: string } | null | undefined;
  const status = err?.status;
  if (opts.gatedList && (status === 403 || status === 404)) return { kind: 'denied' };
  // Employee-targeted read: the 404 is deliberately non-enumerating, so it is NOT reported as a missing permission. An explicit 403 is.
  if (opts.targeted && status === 404) return { kind: 'targetUnavailable' };
  if (opts.targeted && status === 403) return { kind: 'denied' };
  const raw = typeof err?.message === 'string' ? err.message.trim() : '';
  return { kind: 'unavailable', message: raw || 'The request failed.' };
}

/**
 * The caller's `surfaces` flag for a capability: true/false when the server reported it, `undefined` when unknown (older server, or access not resolved yet).
 * Unknown means "do not hide"; the server's answer stays the authority.
 */
export function useSurfaceFlag(flag: CapabilitySurface): boolean | undefined {
  const { state } = useHrAccess();
  return state.kind === 'ready' ? state.me.surfaces[flag] : undefined;
}

export function needsPermissionMessage(label: PermissionLabel, what = 'do this'): string {
  return `You don't have permission to ${what}. It needs the "${label}" permission. Ask an administrator who can change access.`;
}

/**
 * Turn a failed ACTION (save/create/assign) into plain words. A server refusal (403) names the permission instead of showing the raw code
 * (`capability-not-granted`, `...-requires-company-scope`); every other failure keeps the server's own message.
 */
export function actionErrorMessage(e: unknown, label: PermissionLabel, what: string, fallback: string): string {
  const err = e as { status?: number; message?: string } | null | undefined;
  if (err?.status === 403) return needsPermissionMessage(label, what);
  return (typeof err?.message === 'string' && err.message.trim()) || fallback;
}

/** Compact form of classifyLoadError for components that keep a string state: 'denied' | 'target' (non-enumerating 404) | 'error' (the request failed). */
export type LoadErrorKey = 'denied' | 'target' | 'error';
export function loadErrorKey(e: unknown, opts: { gatedList?: boolean; targeted?: boolean }): LoadErrorKey {
  const k = classifyLoadError(e, opts).kind;
  return k === 'denied' ? 'denied' : k === 'targetUnavailable' ? 'target' : 'error';
}

export const TARGET_UNAVAILABLE_TEXT = "This isn't available to you, or the employee isn't available.";
